import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../services/api";
import AppSidebar from "./AppSidebar";
import {
  faHome, faInbox, faTable, faFileAlt, faComments, faList, faCommentDots, faChartBar,
} from "@fortawesome/free-solid-svg-icons";

const API = process.env.REACT_APP_API_URL || '';
const authHeader = () => ({ Authorization: `Token ${localStorage.getItem("Token")}` });

// AudioContext-based beep — once unlocked via a user gesture, plays reliably even in background tabs.
let _audioCtx = null;
let _ctxUnlocked = false;

function getAudioCtx() {
  if (!_audioCtx) {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return _audioCtx;
}

let _keepaliveTimer = null;

// ── Group reminder polling (module-level so it survives React re-renders) ──────
const _REMINDER_KEYS = [
  { key: 'tea_break_time', type: 'tea' },
  { key: 'lunch_time',     type: 'lunch' },
  { key: 'eod_time',       type: 'eod' },
];
let _reminderInterval = null;
let _reminderShown = {};
let _reminderShowFn = null;

async function _checkGroupReminders() {
  const token = localStorage.getItem("Token");
  if (!token || !_reminderShowFn) return;
  const now = new Date();
  // Skip weekends — no reminders on Saturday (6) or Sunday (0)
  if (now.getDay() === 0 || now.getDay() === 6) return;
  const nowMs = now.getHours() * 3600000 + now.getMinutes() * 60000 + now.getSeconds() * 1000;
  const today = now.toDateString();
  try {
    const res = await fetch(`${API}/api/dm/threads/`, { headers: { Authorization: `Token ${token}` } });
    if (!res.ok) return;
    const threads = await res.json();
    for (const t of threads) {
      if (!t.is_group) continue;
      for (const r of _REMINDER_KEYS) {
        const tbt = t.group_settings?.[r.key];
        if (!tbt) continue;
        const [bh, bm] = tbt.split(':').map(Number);
        if (isNaN(bh) || isNaN(bm)) continue;
        const breakMs = bh * 3600000 + bm * 60000;
        const diff = nowMs - breakMs;
        if (diff < 0 || diff >= 300000) continue;
        const key = `${t.id}_${r.key}_${today}`;
        if (_reminderShown[key]) continue;
        _reminderShown[key] = true;
        _reminderShowFn(r.type, t.group_name || 'your group');
        fetch(`${API}/api/dm/threads/${t.id}/fire-alert/`, {
          method: 'POST', headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ alert_type: r.type, alert_id: '' }),
        }).catch(() => {});
      }
      // Custom alerts
      for (const ca of (t.group_settings?.custom_alerts || [])) {
        if (!ca.time) continue;
        const [bh, bm] = ca.time.split(':').map(Number);
        if (isNaN(bh) || isNaN(bm)) continue;
        const baseMs = bh * 3600000 + bm * 60000;
        let dedupeKey;
        let slotIndex;

        if (ca.type === 'once' && ca.date) {
          // One-time: fire any time on the scheduled date after the time (if not yet fired)
          const nowDate = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
          if (nowDate !== ca.date) continue;
          if (nowMs < baseMs) continue; // too early
          const backendFiredKeyOnce = `custom_${ca.id}_${nowDate}`;
          if (t.group_settings?._fired_alerts?.[backendFiredKeyOnce]) continue;
          dedupeKey = `${t.id}_custom_${ca.id}_once`;
        } else if (ca.type === 'repeat' && ca.repeat_hours) {
          // Repeat every N hours starting from base time, for up to 8 hours
          if (nowMs < baseMs) continue;
          const MAX_REPEAT_MS = 8 * 3600000;
          if (nowMs > baseMs + MAX_REPEAT_MS) continue;
          const intervalMs = Number(ca.repeat_hours) * 3600000;
          const elapsed = nowMs - baseMs;
          slotIndex = Math.floor(elapsed / intervalMs);
          const slotStart = baseMs + slotIndex * intervalMs;
          const diff = nowMs - slotStart;
          if (diff < 0 || diff >= 300000) continue;
          dedupeKey = `${t.id}_custom_${ca.id}_${today}_slot${slotIndex}`;
        } else {
          // Daily: fire any time after scheduled time if not yet fired today
          if (nowMs < baseMs) continue; // too early
          const todayISO = now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
          const backendFiredKey = `custom_${ca.id}_${todayISO}`;
          if (t.group_settings?._fired_alerts?.[backendFiredKey]) continue; // already fired today
          dedupeKey = `${t.id}_custom_${ca.id}_${today}`;
        }

        if (_reminderShown[dedupeKey]) continue;
        _reminderShown[dedupeKey] = true;
        _reminderShowFn('custom', t.group_name || 'your group', { emoji: ca.emoji || '🔔', label: ca.label });
        fetch(`${API}/api/dm/threads/${t.id}/fire-alert/`, {
          method: 'POST', headers: { Authorization: `Token ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ alert_type: 'custom', alert_id: ca.id, ...(slotIndex !== undefined && { slot_index: slotIndex }) }),
        }).catch(() => {});
      }
    }
  } catch {}
}

function _startReminderPolling(showFn) {
  _reminderShowFn = showFn; // always update so it has latest React state
  if (_reminderInterval) return; // already ticking
  _checkGroupReminders();
  _reminderInterval = setInterval(_checkGroupReminders, 30000);
}

function unlockAudioCtx() {
  if (_ctxUnlocked) return;
  try {
    const ctx = getAudioCtx();
    // Play a zero-length silent buffer — this "warms up" the context with a user gesture
    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
    if (ctx.state === 'suspended') ctx.resume();
    _ctxUnlocked = true;
    // Chrome auto-suspends AudioContext when no non-zero audio output for ~30s.
    // Play a 10ms burst of a 1Hz tone at gain 0.0001 every 20s — inaudible but non-zero,
    // so Chrome sees it as audio activity and never auto-suspends.
    if (!_keepaliveTimer) {
      _keepaliveTimer = setInterval(() => {
        if (!_audioCtx || _audioCtx.state !== 'running') return;
        try {
          const ctx = _audioCtx;
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          gain.gain.setValueAtTime(0.0001, ctx.currentTime);
          osc.frequency.setValueAtTime(1, ctx.currentTime);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.01);
        } catch {}
      }, 20000);
    }
  } catch {}
}

function _doBeep(ctx) {
  try {
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.35, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.15);
    osc.connect(gain);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
  } catch {}
}

function playBeepViaCtx() {
  try {
    const ctx = getAudioCtx();
    if (ctx.state === 'running') {
      _doBeep(ctx);
    } else {
      // Try to resume — succeeds if there was a recent user gesture
      ctx.resume().then(() => _doBeep(ctx)).catch(() => {});
    }
  } catch {}
}

// Paths where the sidebar icons should render
const JT_SIDEBAR_PATHS = [
  '/projects_list', '/project_inbox', '/create-new', '/edit',
  '/project_detailed_view', '/project_summary', '/discussion',
  '/assign-resource', '/survey_insights', '/survey-list', '/messages',
  '/changelog',
];

const PAGE_LABELS = {
  '/projects_list': 'Home',
  '/project_inbox': 'Inbox',
  '/messages': 'Messages',
  '/discussions': 'Discussions',
  '/project_summary': 'Reports',
  '/survey-list': 'Surveys',
  '/survey_insights': 'Insights',
  '/create-new': 'New Project',
  '/assign-resource': 'Assign',
  '/changelog': 'Change Log',
};

const JTSide = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [dmUnreadCount, setDmUnreadCount] = useState(0);
  // Use refs (not state) so these don't cause the mount effect to re-run on every poll
  const prevTotalUnreadRef = React.useRef(0);
  const prevDmUnreadRef = React.useRef(0);
  const [myStatus, setMyStatus] = useState(() => localStorage.getItem('myStatus') || 'online');
  const [userStatuses, setUserStatuses] = useState({});
  const [toasts, setToasts] = useState([]);

  const userEmail = localStorage.getItem("username");
  const isRamUser = userEmail === "ram@4sight-global.com";
  const originalFaviconRef = React.useRef(null);

  // Draw red badge on the favicon canvas + update document title
  const updateFaviconBadge = useCallback((count) => {
    // Update document title — shows in browser tab and OS taskbar tooltip
    const baseTitle = 'PMS';
    document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${baseTitle}` : baseTitle;

    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx2d = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx2d.clearRect(0, 0, 32, 32);
      ctx2d.drawImage(img, 0, 0, 32, 32);
      if (count > 0) {
        const r = 9;
        const cx = 32 - r - 1;
        const cy = r + 1;
        ctx2d.beginPath();
        ctx2d.arc(cx, cy, r, 0, 2 * Math.PI);
        ctx2d.fillStyle = '#ef4444';
        ctx2d.fill();
        ctx2d.strokeStyle = '#cc0000';
        ctx2d.lineWidth = 1.5;
        ctx2d.stroke();
        ctx2d.fillStyle = '#ffffff';
        const label = count > 99 ? '99+' : String(count);
        ctx2d.font = `bold ${label.length > 1 ? 7 : 9}px Arial`;
        ctx2d.textAlign = 'center';
        ctx2d.textBaseline = 'middle';
        ctx2d.fillText(label, cx, cy + 0.5);
      }
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      if (!originalFaviconRef.current) originalFaviconRef.current = link.href;
      link.href = count > 0 ? canvas.toDataURL('image/png') : (originalFaviconRef.current || '/favicon.png');
    };
    img.onerror = () => {
      // If favicon can't load, still update title so count is visible
    };
    // No crossOrigin — same-origin favicon doesn't need CORS; setting it causes failures
    // if server doesn't return Access-Control-Allow-Origin header
    img.src = '/favicon.png';
  }, []);

  // Update favicon + taskbar icon badge whenever counts change
  useEffect(() => {
    const total = totalUnreadCount + dmUnreadCount;
    updateFaviconBadge(total);
    // setAppBadge shows unread count on the PWA taskbar icon (like Teams)
    if ('setAppBadge' in navigator) {
      if (total > 0) navigator.setAppBadge(total).catch(() => {});
      else navigator.clearAppBadge().catch(() => {});
    }
  }, [totalUnreadCount, dmUnreadCount, updateFaviconBadge]);

  const playNotificationSound = useCallback(() => {
    playBeepViaCtx();
  }, []);

  // On first user interaction: unlock AudioContext + request notification permission
  useEffect(() => {
    const onGesture = () => {
      unlockAudioCtx();
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
      document.removeEventListener('click', onGesture, true);
      document.removeEventListener('keydown', onGesture, true);
    };
    document.addEventListener('click', onGesture, true);
    document.addEventListener('keydown', onGesture, true);
    return () => {
      document.removeEventListener('click', onGesture, true);
      document.removeEventListener('keydown', onGesture, true);
    };
  }, []);

  const showDesktopNotification = useCallback((message) => {
    const id = Date.now();
    setToasts(prev => [...prev.slice(-2), { id, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 5000);
  }, []);

  const showReminderToast = useCallback((type, groupName, customData = null) => {
    const id = Date.now();
    setToasts(prev => [...prev.slice(-2), { id, type, groupName, customData }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 10000);
    playNotificationSound();
  }, [playNotificationSound]);

  // Wire the module-level poller to our React toast function — runs once per session
  useEffect(() => {
    _startReminderPolling(showReminderToast);
  }, [showReminderToast]);

  // Shared 429 back-off: skip this many ticks before next attempt
  const notifBackoffRef = React.useRef(0);

  const fetchTotalUnreadCount = useCallback(async () => {
    if (notifBackoffRef.current > 0) { notifBackoffRef.current--; return; }
    try {
      const username = localStorage.getItem("username");
      if (!username) return;
      const response = await api.get(`discussion/total-unread-count/?username=${username}`);
      const newCount = response.data.total_unread_count || 0;
      const status = localStorage.getItem('myStatus') || 'online';
      if (newCount > prevTotalUnreadRef.current && status !== 'busy') {
        showDesktopNotification(
          newCount === 1 ? "You have a new unread message." : `You have ${newCount} new unread messages.`
        );
        playNotificationSound();
      }
      prevTotalUnreadRef.current = newCount;
      setTotalUnreadCount(newCount);
    } catch (error) {
      if (error.response?.status === 429) {
        notifBackoffRef.current = Math.min((notifBackoffRef.current || 0) + 4, 10);
      }
    }
  }, [showDesktopNotification, playNotificationSound]);

  const fetchDmUnreadCount = useCallback(async () => {
    if (notifBackoffRef.current > 0) return; // shared back-off with total unread
    try {
      const token = localStorage.getItem("Token");
      if (!token) return;
      const res = await fetch(`${API}/api/dm/unread/`, { headers: authHeader() });
      if (res.status === 429) {
        notifBackoffRef.current = Math.min((notifBackoffRef.current || 0) + 4, 10);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        const newDmCount = data.unread || 0;
        const status = localStorage.getItem('myStatus') || 'online';
        if (newDmCount > prevDmUnreadRef.current && status !== 'busy') {
          showDesktopNotification("You have a new direct message.");
          playNotificationSound();
        }
        prevDmUnreadRef.current = newDmCount;
        setDmUnreadCount(newDmCount);
      }
    } catch {}
  }, [playNotificationSound, showDesktopNotification]);


  const sendHeartbeat = useCallback(async (statusOverride) => {
    try {
      const token = localStorage.getItem("Token");
      if (!token) return;
      const status = statusOverride || localStorage.getItem('myStatus') || 'online';
      const pathname = window.location.pathname;
      const page = PAGE_LABELS[pathname] || (pathname !== '/' ? pathname.replace(/^\//, '') : '');
      const body = { status };
      if (page) body.page = page;
      await fetch(`${API}/users/heartbeat/`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {}
  }, []);

  const fetchUserStatuses = useCallback(async () => {
    try {
      const token = localStorage.getItem("Token");
      if (!token) return;
      const res = await fetch(`${API}/users/statuses/`, { headers: authHeader() });
      if (res.ok) {
        const data = await res.json();
        setUserStatuses(data);
        window.__userStatuses = data;
      }
    } catch {}
  }, []);

  const applyStatus = useCallback(async (status) => {
    setMyStatus(status);
    localStorage.setItem('myStatus', status);
    window.dispatchEvent(new CustomEvent('myStatusChanged', { detail: status }));
    try {
      const token = localStorage.getItem("Token");
      if (!token) return;
      await fetch(`${API}/users/set-status/`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
    } catch {}
  }, []);

  // Send heartbeat immediately when user switches back to this tab
  // (Chrome throttles background timers — this keeps status accurate when tab becomes active)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') sendHeartbeat();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [sendHeartbeat]);

  // Log page activity on every route change
  useEffect(() => {
    const token = localStorage.getItem('Token');
    if (!token || !location.pathname || location.pathname === '/') return;
    fetch(`${API}/users/log-activity/`, {
      method: 'POST',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: location.pathname }),
    }).catch(() => {});
  }, [location.pathname]);

  // Handle ?set_status= from PWA taskbar shortcut
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('set_status');
    if (s && ['online', 'busy', 'offline', 'tea_break'].includes(s)) {
      applyStatus(s);
      // Remove param from URL without reload
      const url = new URL(window.location.href);
      url.searchParams.delete('set_status');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, [location.search, applyStatus]);

  useEffect(() => {
    fetchTotalUnreadCount();
    fetchDmUnreadCount();
    fetchUserStatuses();
    sendHeartbeat();

    // Use a Web Worker for the poll timer — Workers are throttled to ~1s minimum
    // even when the browser window is minimized, whereas main-thread setInterval
    // gets throttled to 60s+ by Chrome's intensive throttling.
    let worker = null;
    let statusInterval = null;
    let heartbeatInterval = null;
    let tickCount = 0;

    try {
      worker = new Worker('/poll-worker.js');
      worker.postMessage({ cmd: 'start', interval: 8000 });
      worker.onmessage = () => {
        tickCount++;
        fetchTotalUnreadCount();
        fetchDmUnreadCount();
        if (tickCount % 2 === 0) fetchUserStatuses();   // every ~16s
        if (tickCount % 8 === 0) sendHeartbeat();        // every ~64s
      };
    } catch {
      // Fallback if Worker fails to load (e.g. in dev with hot-reload quirks)
      const id1 = setInterval(fetchTotalUnreadCount, 8000);
      const id2 = setInterval(fetchDmUnreadCount, 8000);
      statusInterval = setInterval(fetchUserStatuses, 16000);
      heartbeatInterval = setInterval(sendHeartbeat, 64000);
      return () => { clearInterval(id1); clearInterval(id2); clearInterval(statusInterval); clearInterval(heartbeatInterval); };
    }

    return () => {
      if (worker) { worker.postMessage({ cmd: 'stop' }); worker.terminate(); }
      if (statusInterval) clearInterval(statusInterval);
      if (heartbeatInterval) clearInterval(heartbeatInterval);
    };
  }, [fetchTotalUnreadCount, fetchDmUnreadCount, fetchUserStatuses, sendHeartbeat]);

  // Listen for avatar clicks from discussion page → navigate to /messages?dm=<userId>
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.id) navigate(`/messages?dm=${e.detail.id}`);
      else navigate('/messages');
    };
    window.addEventListener('openDM', handler);
    return () => window.removeEventListener('openDM', handler);
  }, [navigate]);

  // Immediate re-check when a group reminder time is saved (MessagesPage dispatches this)
  useEffect(() => {
    const handler = () => _checkGroupReminders();
    window.addEventListener('teaBreakTimeSaved', handler);
    return () => window.removeEventListener('teaBreakTimeSaved', handler);
  }, []);

  // When MessagesPage opens a thread and marks messages read, immediately refresh the badge
  useEffect(() => {
    const handler = () => { fetchDmUnreadCount(); };
    window.addEventListener('jtRefreshDm', handler);
    return () => window.removeEventListener('jtRefreshDm', handler);
  }, [fetchDmUnreadCount]);

  const navItems = [
    { href: "/projects_list",   label: "Home",        icon: faHome,     exact: true },
    { href: "/project_inbox",   label: "Inbox",       icon: faInbox,    unreadCount: totalUnreadCount },
    ...(isRamUser ? [{ href: "/assign-resource", label: "Assign", icon: faTable }] : []),
    { href: "/project_summary", label: "Reports",     icon: faFileAlt },
    { href: "/survey-list",     label: "Surveys",     icon: faList },
    ...(isRamUser ? [{ href: "/survey_insights", label: "Insights", icon: faChartBar }] : []),
    { href: "/messages", label: "Messages", icon: faCommentDots, unreadCount: dmUnreadCount },
  ];

  return (
    <>
      {JT_SIDEBAR_PATHS.some(p => location.pathname.startsWith(p)) && (
        <AppSidebar navItems={navItems} topOffset="100px" />
      )}
      {/* In-page notification toasts — shown bottom-right, no browser permission required */}
      <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none' }}>
        {toasts.map(t => {
          const REMINDER_THEMES = {
            tea:   { bg: 'linear-gradient(135deg,#1c0a00,#78350f)', shadow: 'rgba(120,53,15,0.6)', border: 'rgba(251,191,36,0.25)', accent: '#fbbf24', sub: '#d97706', text: '#fef3c7', emoji: '🍵', label: 'TEA BREAK' },
            lunch: { bg: 'linear-gradient(135deg,#052e16,#166534)', shadow: 'rgba(22,101,52,0.6)',  border: 'rgba(134,239,172,0.25)', accent: '#4ade80', sub: '#16a34a', text: '#dcfce7', emoji: '🍱', label: 'LUNCH BREAK' },
            eod:   { bg: 'linear-gradient(135deg,#1e1b4b,#3730a3)', shadow: 'rgba(55,48,163,0.6)',  border: 'rgba(167,139,250,0.25)', accent: '#a78bfa', sub: '#818cf8', text: '#ede9fe', emoji: '🌅', label: 'END OF DAY' },
          };
          if (t.type === 'custom' && t.customData) {
            const cd = t.customData;
            return (
              <div key={t.id} style={{
                background: 'linear-gradient(135deg,#0c1a2e,#1e3a5f)', color: '#e0f2fe',
                borderRadius: 14, padding: '16px 20px',
                boxShadow: '0 8px 32px rgba(14,165,233,0.35), 0 0 0 1px rgba(56,189,248,0.2)',
                display: 'flex', alignItems: 'center', gap: 14,
                minWidth: 260, maxWidth: 320,
                animation: 'jtToastIn 0.35s cubic-bezier(0.34,1.56,0.64,1)',
              }}>
                <span style={{ fontSize: 36, display: 'block', flexShrink: 0, animation: 'cupBob 2.4s ease-in-out infinite' }}>{cd.emoji || '🔔'}</span>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', color: '#38bdf8', marginBottom: 4 }}>REMINDER</div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>{cd.label}</div>
                  <div style={{ fontSize: 11, color: '#7dd3fc', marginTop: 4 }}>{t.groupName}</div>
                </div>
              </div>
            );
          }
          const theme = REMINDER_THEMES[t.type];
          if (theme) return (
            <div key={t.id} style={{
              background: theme.bg, color: theme.text,
              borderRadius: 14, padding: '16px 20px',
              boxShadow: `0 8px 32px ${theme.shadow}, 0 0 0 1px ${theme.border}`,
              display: 'flex', alignItems: 'center', gap: 14,
              minWidth: 260, maxWidth: 320,
              animation: 'jtToastIn 0.35s cubic-bezier(0.34,1.56,0.64,1)',
            }}>
              <div style={{ flexShrink: 0, textAlign: 'center', lineHeight: 1 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.15em', color: theme.accent, marginBottom: 4 }}>
                  <span style={{ display: 'inline-block', animation: 'steamA 1.8s ease-in-out infinite' }}>~</span>
                  <span style={{ display: 'inline-block', animation: 'steamB 1.8s ease-in-out infinite 0.3s' }}>~</span>
                  <span style={{ display: 'inline-block', animation: 'steamA 1.8s ease-in-out infinite 0.6s' }}>~</span>
                </div>
                <span style={{ fontSize: 36, display: 'block', animation: 'cupBob 2.4s ease-in-out infinite' }}>{theme.emoji}</span>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', color: theme.accent, marginBottom: 4 }}>{theme.label}</div>
                <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3 }}>{t.groupName}</div>
                <div style={{ fontSize: 11, color: theme.sub, marginTop: 4 }}>
                  { t.type === 'tea' ? 'Step away and recharge ☕' : t.type === 'lunch' ? 'Time to eat and recharge 🍽️' : 'Time to wrap up and head home 🏠' }
                </div>
              </div>
            </div>
          );
          return (
            <div key={t.id} style={{
              background: '#0B2B2B', color: '#e0f2f1',
              borderRadius: 10, padding: '10px 16px',
              fontSize: 13, fontWeight: 500,
              boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
              display: 'flex', alignItems: 'center', gap: 8,
              maxWidth: 280,
              animation: 'jtToastIn 0.25s ease',
            }}>
              <span style={{ fontSize: 16 }}>🔔</span>
              {t.message}
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes jtToastIn { from { opacity:0; transform:translateY(16px) scale(0.92); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes cupBob { 0%,100% { transform:translateY(0) rotate(-4deg); } 50% { transform:translateY(-6px) rotate(4deg); } }
        @keyframes steamA { 0%,100% { opacity:0; transform:translateY(0) scaleX(1); } 50% { opacity:1; transform:translateY(-8px) scaleX(0.7); } }
        @keyframes steamB { 0%,100% { opacity:0; transform:translateY(0) scaleX(1); } 50% { opacity:0.8; transform:translateY(-10px) scaleX(0.5); } }
      `}</style>
    </>
  );
};

export default JTSide;
